import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Truck, LogIn, Mail, Lock, ShieldCheck, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { API, formatApiError, LOGIN_REASON_KEY, setDeviceToken } from "../auth/api";
import { MfaSetup, RecoveryCodes } from "../components/AccountSecurity";

/** Login page now also handles "Glemt adgangskode" inline so brugeren ikke skal
 *  navigere til en separat URL. Stages:
 *    "login"  -> default form
 *    "forgot" -> indtast email, modtag mail med 72-timers link
 *    "sent"   -> bekræftelse-skærm
 */
export default function LoginPage() {
  const { login, completeLogin } = useAuth();
  const [stage, setStage] = useState("login"); // "login" | "forgot" | "sent" | "mfa" | "mfa_setup" | "mfa_codes"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // 2026-10-25: Hvorfor man blev sendt til login (fx logget ud efter nyt kodeord).
  const [error, setError] = useState(() => { try { const r = sessionStorage.getItem(LOGIN_REASON_KEY) || ""; sessionStorage.removeItem(LOGIN_REASON_KEY); return r; } catch (_) { return ""; } });
  // 2026-10-25: To-trinsbekræftelse efter kodeordet.
  const [mfa, setMfa] = useState(null);          // {mfa_token, can_remember}
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [remember, setRemember] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(null);   // login efter opsætning (vises efter gendannelseskoderne)
  // 2026-10-30: Samme e-mail og kodeord i flere firmaer (fx en afløser hos to vognmænd) - vælg firmaet.
  const [choices, setChoices] = useState([]);

  const finish = (data) => {
    const u = completeLogin(data);
    toast.success(`Velkommen, ${u.name}!`);
    if (data.recovery_left !== undefined && data.recovery_left <= 3) toast.warning(`Du har ${data.recovery_left} gendannelseskoder tilbage - lav nye under Sikkerhed`);
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/mfa/login-verify`, {
        mfa_token: mfa.mfa_token, ...(useRecovery ? { recovery_code: code.trim() } : { code: code.replace(/\s/g, "") }),
        remember_device: !!remember && !!mfa.can_remember });
      if (data.device_token) setDeviceToken(email.trim().toLowerCase(), data.device_token);
      finish(data);
    } catch (err) {
      setError(formatApiError(err));
      if (err?.response?.status === 401 && /log ind igen|tiden er gået/i.test(formatApiError(err))) { setStage("login"); setMfa(null); setCode(""); }
    } finally { setLoading(false); }
  };

  const submitLogin = async (e, companyId = "") => {
    if (e && e.preventDefault) e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !password) {
      setError("Email og adgangskode er påkrævet");
      return;
    }
    setError(""); setLoading(true);
    try {
      const res = await login(cleanEmail, password, companyId);
      if (res?.chooseCompany) { setChoices(res.chooseCompany); setStage("choose"); return; }
      if (res?.mfaStep) {
        setMfa(res.mfaStep); setCode(""); setUseRecovery(false); setPassword("");
        setStage(res.mfaStep.mfa_setup_required ? "mfa_setup" : "mfa");
        return;
      }
      toast.success(`Velkommen, ${res.name}!`);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || formatApiError(err);
      if (status === 429) { setError("⚠️ " + msg); toast.error("For mange forsøg - " + msg); }
      else { setError(msg); toast.error(msg); }
    } finally { setLoading(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) { setError("Indtast din email"); return; }
    setError(""); setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: cleanEmail });
      setStage("sent");
    } catch (err) {
      // Server svarer altid 200 medmindre der er rate-limit/validation, men vis alligevel besked
      const msg = err?.response?.data?.detail || formatApiError(err);
      setError(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="relative min-h-[100dvh] bg-slate-900 flex items-center justify-center p-4 overflow-hidden" data-testid="login-page">

      <div className="pointer-events-none absolute inset-0 opacity-30">
        <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-full bg-orange-500/20 blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 bg-slate-900 text-white px-6 py-3 rounded-xl shadow-lg mb-4 border border-slate-700">
            <Truck className="w-8 h-8 text-orange-500" />
            <span className="font-heading font-black text-2xl">
              Container<span className="text-orange-500">Flow</span>
            </span>
          </div>
          <p className="text-sm text-slate-400">
            {stage === "login" && "Log ind på din konto"}
            {stage === "choose" && "Hvilket firma vil du logge ind i?"}
            {stage === "mfa" && "To-trinsbekræftelse"}
            {(stage === "mfa_setup" || stage === "mfa_codes") && "Sæt to-trinsbekræftelse op"}
            {stage === "forgot" && "Glemt adgangskode"}
            {stage === "sent" && "Tjek din indbakke"}
          </p>
        </div>

        {stage === "choose" ? (
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 space-y-3 border border-slate-200 dark:border-slate-700" data-testid="login-choose">
            <p className="text-sm text-slate-600 dark:text-slate-300">Din e-mail bruges i flere firmaer. Vælg det firma, du vil logge ind i:</p>
            {choices.map((c) => (
              <button key={c.company_id} type="button" disabled={loading} onClick={() => submitLogin(null, c.company_id)} data-testid={`login-choose-${c.company_id}`}
                className="w-full text-left rounded-xl border border-slate-200 dark:border-slate-600 px-4 py-3 hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-slate-700 disabled:opacity-50">
                <div className="font-semibold text-slate-900 dark:text-white">{c.company_name}</div>
                <div className="text-xs text-slate-500">{c.role_label}</div>
              </button>
            ))}
            {error && <p className="text-sm text-rose-600">{error}</p>}
            <button type="button" onClick={() => { setStage("login"); setChoices([]); setPassword(""); setError(""); }} className="text-slate-500 inline-flex items-center gap-1 text-sm"><ArrowLeft className="w-4 h-4" /> Tilbage</button>
          </div>
        ) : stage === "mfa_setup" || stage === "mfa_codes" ? (
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 space-y-4 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white" data-testid="login-mfa-setup">
            {stage === "mfa_codes" && pendingLogin ? (
              <RecoveryCodes codes={pendingLogin.recovery_codes || []} onDone={() => finish(pendingLogin)} />
            ) : (
              <MfaSetup
                intro="Din konto har adgang til løndata, så den skal beskyttes med en kode fra telefonen ud over adgangskoden. Det tager et minut - og kun én gang."
                start={async () => (await axios.post(`${API}/auth/mfa/login-setup`, { mfa_token: mfa.mfa_token })).data}
                confirm={async (c) => (await axios.post(`${API}/auth/mfa/login-setup-confirm`, { mfa_token: mfa.mfa_token, code: c })).data}
                onConfirmed={(data) => { setPendingLogin(data); setStage("mfa_codes"); }} />
            )}
          </div>
        ) : stage === "mfa" ? (
          <form onSubmit={submitMfa} className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 space-y-4 border border-slate-200 dark:border-slate-700" data-testid="login-mfa">
            <div className="flex items-center justify-center gap-2 pb-1">
              <ShieldCheck className="w-6 h-6 text-orange-500" />
              <h1 className="font-heading font-bold text-xl text-slate-900 dark:text-white">{useRecovery ? "Gendannelseskode" : "Kode fra appen"}</h1>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 text-center">
              {useRecovery ? "Skriv en af de gendannelseskoder, du gemte, da du satte 2FA op. Hver kode virker én gang." : "Åbn din godkendelses-app, og skriv den 6-cifrede kode for ContainerFlow."}
            </p>
            <input value={code} onChange={(e) => setCode(e.target.value)} autoFocus disabled={loading}
              inputMode={useRecovery ? "text" : "numeric"} autoComplete="one-time-code" placeholder={useRecovery ? "xxxx-xxxx" : "123456"}
              className="w-full px-4 py-3 border-2 border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl text-xl text-center tracking-[0.3em] font-mono focus:border-orange-500 outline-none"
              data-testid="login-mfa-code" />
            {mfa?.can_remember && !useRecovery && (
              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <input type="checkbox" className="w-4 h-4 accent-orange-500" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="login-mfa-remember" />
                Husk denne enhed i 30 dage (kun på din egen computer)
              </label>
            )}
            {error && <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 px-4 py-2.5 rounded-lg text-sm" data-testid="login-error">{error}</div>}
            <button type="submit" disabled={loading || code.trim().length < 6}
              className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold text-base inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="login-mfa-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Bekræft
            </button>
            <div className="flex justify-between text-xs">
              <button type="button" onClick={() => { setUseRecovery(v => !v); setCode(""); setError(""); }} className="font-semibold text-orange-600 dark:text-orange-400" data-testid="login-mfa-recovery-toggle">
                {useRecovery ? "Brug koden fra appen" : "Mistet telefonen? Brug en gendannelseskode"}
              </button>
              <button type="button" onClick={() => { setStage("login"); setMfa(null); setCode(""); setError(""); }} className="text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Tilbage</button>
            </div>
            <p className="text-[11px] text-slate-400 text-center">Har du hverken telefon eller koder, så bed din administrator om at nulstille din 2FA.</p>
          </form>
        ) : stage === "sent" ? (
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 space-y-5 border border-slate-200 dark:border-slate-700 text-center" data-testid="forgot-sent-card">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <h1 className="font-heading font-bold text-xl text-slate-900 dark:text-white">
              Mail sendt
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              Hvis <span className="font-semibold">{email}</span> findes i ContainerFlow, har vi sendt et link til at vælge ny adgangskode.
              Linket er gyldigt i 72 timer.
            </p>
            <button onClick={() => { setStage("login"); setError(""); setPassword(""); }}
              className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold text-base inline-flex items-center justify-center gap-2"
              data-testid="forgot-back-btn">
              <ArrowLeft className="w-4 h-4" /> Tilbage til login
            </button>
          </div>
        ) : (
          <form
            onSubmit={stage === "forgot" ? submitForgot : submitLogin}
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 space-y-5 border border-slate-200 dark:border-slate-700"
            data-testid={stage === "forgot" ? "forgot-form" : "login-form"}
          >
            <div className="flex items-center justify-center gap-2 pb-2">
              <ShieldCheck className="w-6 h-6 text-orange-500" />
              <h1 className="font-heading font-bold text-xl text-slate-900 dark:text-white">
                {stage === "forgot" ? "Nulstil adgangskode" : "Login"}
              </h1>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="din@email.dk"
                  autoComplete="email"
                  autoFocus
                  disabled={loading}
                  className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl text-base focus:border-orange-500 focus:ring-0 outline-none disabled:opacity-50"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            {stage === "login" && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Adgangskode</label>
                  <button
                    type="button"
                    onClick={() => { setStage("forgot"); setError(""); setPassword(""); }}
                    className="text-xs font-semibold text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
                    data-testid="forgot-password-link"
                  >
                    Glemt adgangskode?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Indtast adgangskode"
                    autoComplete="current-password"
                    disabled={loading}
                    className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl text-base focus:border-orange-500 focus:ring-0 outline-none disabled:opacity-50"
                    data-testid="login-password-input"
                  />
                </div>
              </div>
            )}

            {error && (
              <div
                className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 px-4 py-2.5 rounded-lg text-sm"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold text-base inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-500/20"
              data-testid={stage === "forgot" ? "forgot-submit-btn" : "login-submit-btn"}
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {stage === "forgot" ? "Sender..." : "Logger ind..."}</>
              ) : stage === "forgot" ? (
                <><Mail className="w-4 h-4" /> Send nulstillingslink</>
              ) : (
                <><LogIn className="w-4 h-4" /> Log ind</>
              )}
            </button>

            {stage === "forgot" && (
              <button
                type="button"
                onClick={() => { setStage("login"); setError(""); }}
                className="w-full text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 inline-flex items-center justify-center gap-1.5"
                data-testid="forgot-cancel-btn"
              >
                <ArrowLeft className="w-3 h-3" /> Tilbage til login
              </button>
            )}
          </form>
        )}

        <div className="mt-6 text-center text-xs text-slate-500">
          ContainerFlow Multi-tenant Platform
        </div>
      </div>
    </div>
  );
}
